/**
 * Exit conditions subsection for cadence profile modals.
 * Shows configured auto-removal rules with badges.
 * The parent is responsible for the collapsible header — this renders only the body.
 */
import PropTypes from 'prop-types';

const EXIT_CONDITION_INFO = {
  email_bounced: { badge: 'EMAIL BOUNCE', description: 'Email bounces too many times' },
  unsubscribed: { badge: 'UNSUBSCRIBED', description: 'Contact unsubscribes from emails' },
  gift_made: { badge: 'GIFT GIVEN', description: 'A qualifying gift is made' },
  portfolio_stage_reached: { badge: 'STAGE CHANGE', description: 'Portfolio stage changes' },
  cadence_qualification: { badge: 'CADENCE MATCH', description: 'Contact is enrolled in another cadence' },
  list_membership: { badge: 'ADDED TO LIST', description: 'Contact is added to a list' },
  saved_search: { badge: 'SAVED SEARCH', description: 'Contact qualifies for a saved search' },
  saved_search_no_longer_match: {
    badge: 'SAVED SEARCH',
    description: 'Contact no longer matches saved search criteria',
  },
  interaction_created: { badge: 'INTERACTION', description: 'An interaction is logged' },
};

const ExitConditionsSection = ({ rules, contactName, cadenceName }) => {
  const firstName = (contactName || '').split(' ')[0] || 'This contact';

  return (
    <div className="cadence-profile-modal--exit-body">
      <p className="cadence-profile-modal--exit-intro">
        If {firstName} triggers any of the following, they will automatically be removed from{' '}
        <strong>{cadenceName}</strong>.
      </p>
      <div className="cadence-profile-modal--exit-rules">
        {rules.map((rule) => {
          const info = EXIT_CONDITION_INFO[rule.removalType] || {
            badge: (rule.removalType || 'EXIT').replace(/_/g, ' ').toUpperCase(),
            description: 'A configured exit condition is triggered',
          };
          const config = rule.removalConfig || {};
          let description = info.description;
          if (rule.removalType === 'portfolio_stage_reached') {
            const stage = config.stageName || config.stage_name;
            const team = config.teamName || config.team_name;
            if (stage && team) description = `Portfolio stage moves to "${stage}" in "${team}"`;
            else if (stage) description = `Portfolio stage moves to "${stage}"`;
          } else if (rule.removalType === 'list_membership') {
            const list = config.listName || config.list_name;
            if (list) description = `Contact is added to list "${list}"`;
          } else if (rule.removalType === 'cadence_qualification') {
            const cad = config.cadenceName || config.cadence_name;
            if (cad) description = `Contact is enrolled in cadence "${cad}"`;
          } else if (rule.removalType === 'saved_search' || rule.removalType === 'saved_search_no_longer_match') {
            const ss = config.savedSearchName || config.saved_search_name;
            if (ss) description = `Contact qualifies for saved search "${ss}"`;
          } else if (rule.removalType === 'email_bounced') {
            const threshold = config.threshold || config.bounceThreshold;
            if (threshold) description = `Email bounces ${threshold} or more times`;
          } else if (rule.removalType === 'interaction_created') {
            const intType = config.interactionTypeName || config.interactionType || config.interaction_type;
            if (intType) description = `A "${intType}" interaction is logged`;
          } else if (rule.removalType === 'gift_made') {
            const amt = config.minAmount || config.min_amount;
            if (amt) description = `A gift of $${Number(amt).toLocaleString()} or more is made`;
          }
          return (
            <div key={rule.id} className="cadence-profile-modal--exit-rule">
              <span className="cadence-profile-modal--exit-badge">{info.badge}</span>
              <span className="cadence-profile-modal--exit-desc">{description}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ExitConditionsSection.propTypes = {
  rules: PropTypes.arrayOf(PropTypes.object).isRequired,
  contactName: PropTypes.string,
  cadenceName: PropTypes.string,
};

export default ExitConditionsSection;
